import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { api } from '../lib/api'

export default function Playlists() {
  const navigate = useNavigate()
  const [playlists, setPlaylists] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [showPanel, setShowPanel] = useState<'none' | 'import' | 'create'>('none')
  const [playlistUrl, setPlaylistUrl] = useState('')
  const [collectionName, setCollectionName] = useState('')
  const [importing, setImporting] = useState(false)
  const [creating, setCreating] = useState(false)
  const [error, setError] = useState('')

  async function loadPlaylists() {
    try {
      const data = await api.getPlaylists()
      setPlaylists(data)
    } catch (err) {
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { loadPlaylists() }, [])

  async function handleImport() {
    if (!playlistUrl.trim()) return
    setImporting(true)
    setError('')
    try {
      await api.importPlaylist(playlistUrl.trim())
      setPlaylistUrl('')
      setShowPanel('none')
      loadPlaylists()
    } catch (err: any) {
      let msg = 'فشل الاستيراد'
      try { msg = JSON.parse(err.message).error || msg } catch {}
      setError(msg)
    } finally {
      setImporting(false)
    }
  }

  async function handleCreate() {
    if (!collectionName.trim()) return
    setCreating(true)
    setError('')
    try {
      const result = await api.createCollection(collectionName.trim())
      setCollectionName('')
      setShowPanel('none')
      navigate(`/playlists/${result.id}`)
    } catch (err: any) {
      let msg = 'فشل الإنشاء'
      try { msg = JSON.parse(err.message).error || msg } catch {}
      setError(msg)
    } finally {
      setCreating(false)
    }
  }

  if (loading) return <div className="text-center text-gray-400 py-12">جاري التحميل...</div>

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-vertex-gold">المجموعات ({playlists.length})</h2>
        <div className="flex gap-2">
          <button
            onClick={() => setShowPanel(showPanel === 'create' ? 'none' : 'create')}
            className="bg-vertex-surface text-white px-4 py-2 rounded text-sm font-bold hover:bg-vertex-surface/80 transition-colors border border-vertex-gold/30"
          >
            + مجموعة جديدة
          </button>
          <button
            onClick={() => setShowPanel(showPanel === 'import' ? 'none' : 'import')}
            className="bg-vertex-gold text-vertex-deep px-4 py-2 rounded text-sm font-bold hover:bg-vertex-gold-light transition-colors"
          >
            + استيراد Playlist
          </button>
        </div>
      </div>

      {/* Import Playlist Form */}
      {showPanel === 'import' && (
        <div className="bg-vertex-navy rounded-lg p-4 border border-vertex-gold/30 mb-6">
          <h3 className="text-vertex-gold text-sm font-bold mb-2">استيراد قائمة تشغيل من YouTube</h3>
          <p className="text-gray-400 text-xs mb-3">الصق رابط قائمة التشغيل وهيتم سحب كل الفيديوهات تلقائياً</p>
          <div className="flex gap-3">
            <input
              value={playlistUrl}
              onChange={(e) => setPlaylistUrl(e.target.value)}
              placeholder="https://www.youtube.com/playlist?list=PLxxxxxxx"
              dir="ltr"
              className="flex-1 bg-vertex-deep border border-vertex-surface rounded px-3 py-2 text-sm text-white placeholder-gray-600 focus:border-vertex-gold outline-none font-mono"
            />
            <button
              onClick={handleImport}
              disabled={importing || !playlistUrl.trim()}
              className="bg-vertex-gold text-vertex-deep px-6 py-2 rounded text-sm font-bold hover:bg-vertex-gold-light disabled:opacity-50 transition-colors whitespace-nowrap"
            >
              {importing ? 'جاري الاستيراد...' : 'استيراد'}
            </button>
          </div>
          {error && <div className="mt-3 bg-red-900/30 border border-red-800 rounded px-3 py-2 text-xs text-red-400">{error}</div>}
        </div>
      )}

      {/* Create Collection Form */}
      {showPanel === 'create' && (
        <div className="bg-vertex-navy rounded-lg p-4 border border-vertex-gold/30 mb-6">
          <h3 className="text-vertex-gold text-sm font-bold mb-2">إنشاء مجموعة جديدة</h3>
          <p className="text-gray-400 text-xs mb-3">أنشئ مجموعة فاضية وبعدين أضف فيها فيديوهات فردي أو bulk</p>
          <div className="flex gap-3">
            <input
              value={collectionName}
              onChange={(e) => setCollectionName(e.target.value)}
              placeholder="اسم المجموعة (مثال: SHRM Round 5)"
              className="flex-1 bg-vertex-deep border border-vertex-surface rounded px-3 py-2 text-sm text-white placeholder-gray-600 focus:border-vertex-gold outline-none"
            />
            <button
              onClick={handleCreate}
              disabled={creating || !collectionName.trim()}
              className="bg-vertex-gold text-vertex-deep px-6 py-2 rounded text-sm font-bold hover:bg-vertex-gold-light disabled:opacity-50 transition-colors whitespace-nowrap"
            >
              {creating ? 'جاري الإنشاء...' : 'إنشاء'}
            </button>
          </div>
          {error && <div className="mt-3 bg-red-900/30 border border-red-800 rounded px-3 py-2 text-xs text-red-400">{error}</div>}
        </div>
      )}

      {/* Playlists Grid */}
      {playlists.length === 0 ? (
        <div className="bg-vertex-navy rounded-lg border border-vertex-surface p-12 text-center">
          <div className="text-4xl mb-4">📺</div>
          <p className="text-gray-400 text-sm mb-2">لا توجد مجموعات بعد</p>
          <p className="text-gray-500 text-xs">استورد playlist من YouTube أو أنشئ مجموعة يدوية</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {playlists.map((pl) => {
            const lectureCount = pl.lecture_count || pl.video_count || 0
            const completedCount = pl.completed_count || 0
            const progress = lectureCount > 0 ? Math.round((completedCount / lectureCount) * 100) : 0

            return (
              <Link
                key={pl.id}
                to={`/playlists/${pl.id}`}
                className="bg-vertex-navy rounded-lg border border-vertex-surface hover:border-vertex-gold/50 transition-all overflow-hidden group"
              >
                {pl.thumbnail_url ? (
                  <div className="aspect-video bg-vertex-deep overflow-hidden">
                    <img src={pl.thumbnail_url} alt={pl.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                  </div>
                ) : (
                  <div className="aspect-video bg-vertex-deep flex items-center justify-center">
                    <span className="text-3xl">{pl.playlist_id ? '📺' : '📁'}</span>
                  </div>
                )}

                <div className="p-4">
                  <h3 className="text-sm font-bold text-white mb-1 line-clamp-2">{pl.name}</h3>
                  <div className="flex items-center justify-between text-xs text-gray-400 mb-3">
                    <span>{lectureCount} محاضرة</span>
                    <span>{completedCount} مكتملة</span>
                  </div>
                  <div className="w-full h-1.5 bg-vertex-surface rounded-full overflow-hidden">
                    <div className="h-full bg-vertex-gold rounded-full transition-all" style={{ width: `${progress}%` }} />
                  </div>
                  <div className="text-xs text-gray-500 mt-1 text-left" dir="ltr">{progress}%</div>
                </div>
              </Link>
            )
          })}
        </div>
      )}
    </div>
  )
}
